import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import 'leaflet/dist/leaflet.css';
import { API_BASE, fetchCsrfToken } from './adminShared';
import './ForestMap.css';

// --- Constants ---

const STORAGE_KEY = 'fm_last_viewport';
const FALLBACK_CENTER = [40.0, -105.5]; // Front Range, CO
const FALLBACK_ZOOM = 7;
const GEOLOCATE_ZOOM = 10;

const STATUS_COLORS = {
  healthy_monitoring: '#1a6b3c',
  suspected: '#c47f17',
  confirmed_active: '#c1362c',
  dead_standing: '#5a5a5a',
  dead_fallen: '#2f2f2f',
  hazard: '#7a1f13',
  treated: '#3968b0',
};

const STATUS_ORDER = [
  'suspected',
  'confirmed_active',
  'dead_standing',
  'dead_fallen',
  'hazard',
  'treated',
  'healthy_monitoring',
];

const STATUS_LABELS = {
  healthy_monitoring: 'Healthy',
  suspected: 'Suspected',
  confirmed_active: 'Active',
  dead_standing: 'Dead (std)',
  dead_fallen: 'Dead (fln)',
  hazard: 'Hazard',
  treated: 'Treated',
};

const SPECIES_OPTIONS = [
  ['ponderosa', 'Ponderosa pine'],
  ['lodgepole', 'Lodgepole pine'],
  ['limber', 'Limber pine'],
  ['bristlecone', 'Bristlecone pine'],
  ['whitebark', 'Whitebark pine'],
  ['scotch', 'Scotch pine'],
  ['douglas_fir', 'Douglas-fir'],
  ['engelmann_spruce', 'Engelmann spruce'],
  ['blue_spruce', 'Blue spruce'],
  ['subalpine_fir', 'Subalpine fir'],
  ['aspen', 'Aspen'],
  ['juniper', 'Juniper'],
  ['other', 'Other / unknown'],
];

const SPONSOR_TYPE_OPTIONS = [
  ['', '— unspecified —'],
  ['corporate', 'Corporate'],
  ['government', 'Government'],
  ['nonprofit', 'Nonprofit'],
  ['foundation', 'Foundation / grant'],
  ['individual', 'Individual / donor'],
  ['other', 'Other'],
];

// Layer order determines the picker's left-to-right button order. Esri first =
// default (sharper deep-zoom, no fallback tricks). Matches the field tool's
// picker for cross-app consistency.
const LAYERS = {
  esri: {
    label: 'Esri',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles © Esri',
    maxZoom: 19,
  },
  naip: {
    label: 'USGS',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Imagery © USGS · NAIP for CONUS',
    maxZoom: 19,
    // USGSImageryOnly's native max is 16. Leaflet upscales past that instead
    // of 404ing on tiles that don't exist server-side.
    maxNativeZoom: 16,
  },
  osm: {
    label: 'Street',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19,
  },
};

// --- Helpers ---

function bboxAreaKm2(bounds) {
  if (!bounds) return 0;
  const latSpan = bounds.latMax - bounds.latMin;
  const lngSpan = bounds.lngMax - bounds.lngMin;
  const avgLat = (bounds.latMax + bounds.latMin) / 2;
  const heightKm = latSpan * 111.0;
  const widthKm = lngSpan * 111.0 * Math.cos((avgLat * Math.PI) / 180);
  return Math.abs(heightKm * widthKm);
}

const AREA_CAP_KM2 = 200;
const AREA_MIN_KM2 = 0.5;

function readLastViewport() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (
      typeof v.lat === 'number' &&
      typeof v.lng === 'number' &&
      typeof v.zoom === 'number'
    ) return v;
  } catch { /* ignore */ }
  return null;
}

function saveLastViewport(lat, lng, zoom) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ lat, lng, zoom }));
  } catch { /* ignore */ }
}

// --- Child components that need map context ---

function ViewportSync({ onBoundsChange, onViewportPersist }) {
  const debounceRef = useRef(null);
  const map = useMapEvents({
    moveend: () => triggerSync(),
    zoomend: () => triggerSync(),
  });

  function triggerSync() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const b = map.getBounds();
      const c = map.getCenter();
      const z = map.getZoom();
      onBoundsChange({
        latMin: b.getSouth(), lngMin: b.getWest(),
        latMax: b.getNorth(), lngMax: b.getEast(),
      });
      onViewportPersist(c.lat, c.lng, z);
    }, 250);
  }

  useEffect(() => {
    const b = map.getBounds();
    onBoundsChange({
      latMin: b.getSouth(), lngMin: b.getWest(),
      latMax: b.getNorth(), lngMax: b.getEast(),
    });
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function MapClickHandler({ onMapClick, enabled }) {
  useMapEvents({
    click: (e) => {
      if (enabled) onMapClick(e.latlng);
    },
  });
  return null;
}

function GeolocateButton({ mapRef }) {
  const [busy, setBusy] = useState(false);

  const locate = () => {
    if (!navigator.geolocation || busy) return;
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (mapRef.current) {
          mapRef.current.flyTo([coords.latitude, coords.longitude], GEOLOCATE_ZOOM, { duration: 0.8 });
        }
        setBusy(false);
      },
      () => setBusy(false),
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 60000 }
    );
  };

  return (
    <button
      type="button"
      className="fm-map-btn fm-locate-btn"
      onClick={locate}
      disabled={busy}
      title="My location"
      aria-label="Center map on my location"
    >
      {busy ? '…' : '📍'}
    </button>
  );
}

// --- Draft-pin icon (avoids Leaflet's broken default marker URL) ---

const draftIcon = L.divIcon({
  className: 'fm-draft-marker',
  html: '<div class="fm-draft-marker-inner"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// --- Observation marker with source-differentiated ring ---

function ObservationMarker({ obs }) {
  const color = STATUS_COLORS[obs.status] || '#666';
  const isNdmi = obs.source === 'ndmi';
  return (
    <>
      {isNdmi && (
        <CircleMarker
          center={[Number(obs.latitude), Number(obs.longitude)]}
          radius={9}
          pathOptions={{
            color: '#ffffff', weight: 2,
            fillColor: color, fillOpacity: 0,
          }}
          interactive={false}
        />
      )}
      <CircleMarker
        center={[Number(obs.latitude), Number(obs.longitude)]}
        radius={6}
        pathOptions={{
          color: '#ffffff', weight: 1.5,
          fillColor: color, fillOpacity: 0.9,
        }}
      >
        <Popup>
          <div className="fm-pop">
            <div className="fm-pop-title">
              <span className="fm-pop-swatch" style={{ backgroundColor: color }} />
              {obs.species} — {obs.status.replace(/_/g, ' ')}
            </div>
            <div className="fm-pop-meta">
              Observed {new Date(obs.observed_at).toLocaleDateString()}
            </div>
            <div className="fm-pop-meta">
              Source: <strong>{obs.source}</strong>
            </div>
            {obs.sponsor && (
              <div className="fm-pop-meta">
                Sponsor: <strong>{obs.sponsor}</strong>
                {obs.sponsor_type && <> ({obs.sponsor_type.replace(/_/g, ' ')})</>}
              </div>
            )}
            {obs.notes && <div className="fm-pop-notes">{obs.notes}</div>}
          </div>
        </Popup>
      </CircleMarker>
    </>
  );
}

// --- Draft pin popup form ---

function DraftPinForm({ latlng, onCancel, onSaved }) {
  const [species, setSpecies] = useState('lodgepole');
  const [status, setStatus] = useState('suspected');
  const [sponsor, setSponsor] = useState('');
  const [sponsorType, setSponsorType] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const csrf = await fetchCsrfToken();
      const res = await fetch(`${API_BASE}/admin/trees/manual/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrf,
        },
        body: JSON.stringify({
          observed_at: new Date().toISOString(),
          latitude: latlng.lat.toFixed(7),
          longitude: latlng.lng.toFixed(7),
          species,
          status,
          confidence: 'field_verified',
          sponsor: sponsor.trim(),
          sponsor_type: sponsorType,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || JSON.stringify(data) || 'Save failed');
      }
      const created = await res.json();
      onSaved(created);
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fm-draft-form">
      <div className="fm-draft-coords">
        {latlng.lat.toFixed(5)}, {latlng.lng.toFixed(5)}
      </div>

      <label className="fm-draft-label">Species</label>
      <Form.Select
        size="sm"
        value={species}
        onChange={(e) => setSpecies(e.target.value)}
        disabled={saving}
      >
        {SPECIES_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </Form.Select>

      <label className="fm-draft-label mt-2">Status</label>
      <div className="fm-status-chips">
        {STATUS_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            className={`fm-status-chip ${status === s ? 'active' : ''}`}
            style={{
              backgroundColor: status === s ? STATUS_COLORS[s] : 'transparent',
              borderColor: STATUS_COLORS[s],
              color: status === s ? '#fff' : STATUS_COLORS[s],
            }}
            onClick={() => setStatus(s)}
            disabled={saving}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      <label className="fm-draft-label mt-2">Sponsor (optional)</label>
      <Form.Control
        size="sm"
        type="text"
        value={sponsor}
        onChange={(e) => setSponsor(e.target.value)}
        placeholder="Company / organization"
        maxLength={200}
        disabled={saving}
      />

      <label className="fm-draft-label mt-2">Sponsor type</label>
      <Form.Select
        size="sm"
        value={sponsorType}
        onChange={(e) => setSponsorType(e.target.value)}
        disabled={saving}
      >
        {SPONSOR_TYPE_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </Form.Select>

      {err && <div className="fm-draft-error">{err}</div>}

      <div className="fm-draft-actions mt-2">
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save observation'}
        </Button>
      </div>
    </div>
  );
}

// --- Main map component ---

function ForestMap({ onOpenRun }) {
  const initialViewport = useMemo(() => readLastViewport(), []);
  const initialCenter = initialViewport
    ? [initialViewport.lat, initialViewport.lng]
    : FALLBACK_CENTER;
  const initialZoom = initialViewport ? initialViewport.zoom : FALLBACK_ZOOM;

  const [activeLayer, setActiveLayer] = useState('esri');
  const [observations, setObservations] = useState([]);
  const [obsLoading, setObsLoading] = useState(false);
  const [draftPin, setDraftPin] = useState(null);
  const [addMode, setAddMode] = useState(false);
  const [flash, setFlash] = useState(null);
  const [viewportArea, setViewportArea] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);

  const boundsRef = useRef(null);
  const fetchDebounce = useRef(null);
  const mapRef = useRef(null);

  // Non-blocking geolocation ONLY if no saved viewport
  useEffect(() => {
    if (initialViewport || !navigator.geolocation) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (cancelled) return;
        if (mapRef.current) {
          mapRef.current.flyTo([coords.latitude, coords.longitude], GEOLOCATE_ZOOM, { duration: 0.6 });
        }
      },
      () => { /* denied/error — fallback view already showing */ },
      { enableHighAccuracy: false, timeout: 4000, maximumAge: 300000 }
    );
    return () => { cancelled = true; };
  }, [initialViewport]);

  const fetchObs = useCallback(async (bounds) => {
    setObsLoading(true);
    try {
      const bbox = `${bounds.latMin},${bounds.lngMin},${bounds.latMax},${bounds.lngMax}`;
      const res = await fetch(
        `${API_BASE}/admin/trees/?bbox=${bbox}&page_size=200`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setObservations(data.results || data);
      }
    } catch { /* silent */ } finally {
      setObsLoading(false);
    }
  }, []);

  const handleBoundsChange = useCallback((bounds) => {
    boundsRef.current = bounds;
    setViewportArea(bboxAreaKm2(bounds));
    if (fetchDebounce.current) clearTimeout(fetchDebounce.current);
    fetchDebounce.current = setTimeout(() => fetchObs(bounds), 350);
  }, [fetchObs]);

  const handleViewportPersist = useCallback((lat, lng, zoom) => {
    saveLastViewport(lat, lng, zoom);
  }, []);

  const handleMapClick = useCallback((latlng) => {
    if (!addMode) return;
    setDraftPin(latlng);
  }, [addMode]);

  const handleSaved = (obs) => {
    setDraftPin(null);
    setAddMode(false);
    setFlash({ kind: 'ok', msg: 'Observation saved.' });
    setTimeout(() => setFlash(null), 2500);
    if (boundsRef.current) fetchObs(boundsRef.current);
  };

  const analyzable =
    !analyzing &&
    boundsRef.current &&
    viewportArea >= AREA_MIN_KM2 &&
    viewportArea <= AREA_CAP_KM2;

  const analyzeReason = () => {
    if (!boundsRef.current) return 'Map still loading.';
    if (viewportArea > AREA_CAP_KM2) {
      return `Zoom in — area is ${viewportArea.toFixed(0)} km², cap is ${AREA_CAP_KM2}.`;
    }
    if (viewportArea < AREA_MIN_KM2) {
      return `Zoom out — area is ${viewportArea.toFixed(2)} km², minimum is ${AREA_MIN_KM2}.`;
    }
    return `Run NDMI analysis on this view (${viewportArea.toFixed(1)} km²).`;
  };

  const runAnalysis = async () => {
    if (!boundsRef.current || analyzing) return;
    setAnalyzing(true);
    setFlash(null);
    try {
      const csrf = await fetchCsrfToken();
      const res = await fetch(`${API_BASE}/admin/analysis-runs/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
        body: JSON.stringify({
          lat_min: boundsRef.current.latMin,
          lng_min: boundsRef.current.lngMin,
          lat_max: boundsRef.current.latMax,
          lng_max: boundsRef.current.lngMax,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');
      setFlash({
        kind: 'ok',
        msg: `${data.candidate_count} candidates from ${data.scene_count} scenes.`,
        action: onOpenRun ? { label: 'Review', run_id: data.id } : null,
      });
    } catch (e) {
      setFlash({ kind: 'err', msg: e.message || 'Analysis failed' });
    } finally {
      setAnalyzing(false);
    }
  };

  const layer = LAYERS[activeLayer];

  return (
    <div className="fm-map-wrap">
      {/* Overlay toolbar */}
      <div className="fm-map-toolbar">
        <Button
          size="sm"
          variant={addMode ? 'warning' : 'primary'}
          onClick={() => { setAddMode((v) => !v); setDraftPin(null); }}
        >
          {addMode ? 'Cancel add' : '+ Add observation'}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={runAnalysis}
          disabled={!analyzable}
          title={analyzeReason()}
          className="ms-2"
        >
          {analyzing
            ? 'Analyzing…'
            : `Analyze this view${viewportArea > 0 ? ` (${viewportArea.toFixed(0)} km²)` : ''}`}
        </Button>
        {obsLoading && <Spinner animation="border" size="sm" className="ms-2" />}
        {flash && (
          <span className={`fm-flash fm-flash-${flash.kind} ms-2`}>
            {flash.msg}
            {flash.action && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 ms-2 align-baseline"
                onClick={() => onOpenRun(flash.action.run_id)}
              >
                {flash.action.label} →
              </button>
            )}
          </span>
        )}
      </div>

      {/* Layer selector */}
      <div className="fm-layer-toggle">
        {Object.entries(LAYERS).map(([k, l]) => (
          <button
            key={k}
            type="button"
            className={`fm-map-btn fm-layer-btn ${activeLayer === k ? 'active' : ''}`}
            onClick={() => setActiveLayer(k)}
          >
            {l.label}
          </button>
        ))}
      </div>

      <GeolocateButton mapRef={mapRef} />

      {addMode && (
        <div className="fm-add-hint">Click the map to place a pin.</div>
      )}

      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        className="fm-map"
        ref={mapRef}
      >
        <TileLayer
          key={activeLayer}
          url={layer.url}
          attribution={layer.attribution}
          maxZoom={layer.maxZoom}
          maxNativeZoom={layer.maxNativeZoom}
        />

        <ViewportSync
          onBoundsChange={handleBoundsChange}
          onViewportPersist={handleViewportPersist}
        />
        <MapClickHandler onMapClick={handleMapClick} enabled={addMode} />

        {observations.map((obs) => (
          <ObservationMarker key={obs.id} obs={obs} />
        ))}

        {draftPin && (
          <Marker position={[draftPin.lat, draftPin.lng]} icon={draftIcon}>
            <Popup
              closeButton={false}
              autoClose={false}
              closeOnClick={false}
              onClose={() => setDraftPin(null)}
            >
              <DraftPinForm
                latlng={draftPin}
                onCancel={() => setDraftPin(null)}
                onSaved={handleSaved}
              />
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
  );
}

export default ForestMap;
